import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useFacilityStore, STATUS, MODES } from '../store/facilityStore'
import DefoliationModal from './DefoliationModal'
import SprayLogModal from './SprayLogModal'
import SprayLogList from './SprayLogList'
import TransferModal from './TransferModal'
import NetModal from './NetModal'
import PreVegZoneMap from './PreVegZoneMap'
import { getRelevantSops, getSopForOverlay } from '../data/sops'
import { apiFetch, uploadPhoto, getToken } from '../lib/apiFetch'
import { SYMBOL_ITEMS } from '../data/overlaySymbols'
import { CheckIcon, CloseIcon, BoardIcon, PlantIcon, WindIcon, PencilIcon, GlovesIcon, GogglesIcon, BootsIcon, MaskIcon, VestIcon, ShieldIcon, WarningIcon, BugIcon } from './icons/Icons'

const STATUS_LABEL = {
  [STATUS.NORMAL]: 'NORMAL',
  [STATUS.WARN]:   'ISSUE ACTIVE',
  [STATUS.ALERT]:  'ALERT',
  [STATUS.IDLE]:   'IDLE',
}

const TYPE_COLOR = {
  flower:  { label: '#4ade80', border: '#1a4d2a', bg: '#0a1f12' },
  veg:     { label: '#86cf36', border: '#2d5010', bg: '#111c08' },
  support: { label: '#60a5fa', border: '#1a3566', bg: '#091427' },
  utility: { label: '#a78bfa', border: '#3a2d6a', bg: '#14102a' },
}

const STATUS_COLORS_BY_THEME = {
  'night-mode': {
    [STATUS.NORMAL]: { label: '#4ade80', border: '#1a4d2a', bg: '#0a1f12' },
    [STATUS.WARN]:   { label: '#facc15', border: '#6b5c00', bg: '#1a1600' },
    [STATUS.ALERT]:  { label: '#f87171', border: '#7a1a1a', bg: '#1f0909' },
    [STATUS.IDLE]:   { label: '#6b7280', border: '#2a2d35', bg: '#0d0f13' },
  },
  'gas-n-up': {
    [STATUS.NORMAL]: { label: '#f97316', border: '#7a3010', bg: '#1a0e06' },
    [STATUS.WARN]:   { label: '#fbbf24', border: '#6b5000', bg: '#1a1200' },
    [STATUS.ALERT]:  { label: '#c084fc', border: '#5b21b6', bg: '#180d2e' },
    [STATUS.IDLE]:   { label: '#6b7280', border: '#2a2d1a', bg: '#0d0f08' },
  },
  'frostd-flakes': {
    [STATUS.NORMAL]: { label: '#15803d', border: '#86efac', bg: '#dcfce7' },
    [STATUS.WARN]:   { label: '#b45309', border: '#fcd34d', bg: '#fef9c3' },
    [STATUS.ALERT]:  { label: '#b91c1c', border: '#fca5a5', bg: '#fee2e2' },
    [STATUS.IDLE]:   { label: '#4b5563', border: '#d1d5db', bg: '#f3f4f6' },
  },
  'bright-mode': {
    [STATUS.NORMAL]: { label: '#166534', border: '#86efac', bg: '#dcfce7' },
    [STATUS.WARN]:   { label: '#92400e', border: '#fcd34d', bg: '#fef9c3' },
    [STATUS.ALERT]:  { label: '#991b1b', border: '#fca5a5', bg: '#fee2e2' },
    [STATUS.IDLE]:   { label: '#374151', border: '#d1d5db', bg: '#f3f4f6' },
  },
}

function useTheme() {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme || 'night-mode')
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.documentElement.dataset.theme || 'night-mode'))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])
  return theme
}

const MODE_OPTIONS = [
  { value: MODES.OFF,  label: 'Off' },
  { value: MODES.AUTO, label: 'Auto' },
  { value: MODES.CROP, label: 'Crop' },
  { value: MODES.FILL, label: 'Fill' },
]

const SYMBOL_DISPLAY = SYMBOL_ITEMS.reduce((acc, item) => ({ ...acc, [item.key]: item }), {})

const DRAWER_TABS = ['OVERVIEW', 'TASKS', 'SPRAY LOGS', 'CHECKS', 'PPE', 'SOPs', 'TABLES']

// ── Yes/No quick log ───────────────────────────────────────────────────────
function YesNoLog({ label, apiPath, bodyFn }) {
  const authUser    = useFacilityStore(s => s.authUser)
  const [submitting, setSubmitting] = useState(false)
  const [saved, setSaved]           = useState(null)
  const [error, setError]           = useState(null)
  const displayName = authUser?.displayName ?? authUser?.primaryEmail ?? 'Unknown'

  const handleTap = async (completed) => {
    setSubmitting(true); setError(null)
    const now = new Date()
    try {
      if (API_BASE) await apiFetch(apiPath, { method: 'POST', body: JSON.stringify(bodyFn(completed)) })
      setSaved({ completed, by: displayName, at: now })
      setTimeout(() => setSaved(null), 5000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (saved) return (
    <div className="yn-confirm" style={{ color: saved.completed ? '#4ade80' : '#f87171' }}>
      <div className="yn-confirm-status">
        {saved.completed ? <><CheckIcon width="16" height="16" style={{marginRight: 4}} /> {label} — Complete</> : <><CloseIcon width="16" height="16" style={{marginRight: 4}} /> {label} — Not Done</>}
      </div>
      <div className="yn-confirm-meta">
        <span className="yn-confirm-by">{saved.by}</span>
        <span className="yn-confirm-at">
          {saved.at.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: false })}
          {' · '}{saved.at.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
        </span>
      </div>
    </div>
  )

  return (
    <div className="checks-yn-block">
      <p className="yn-prompt">{label} complete?</p>
      <div className="yn-signed-as">Signing as <strong>{displayName}</strong></div>
      {error && <p className="form-error">{error}</p>}
      <div className="yn-btn-row">
        <button className="yn-btn yn-btn--yes" disabled={submitting} onClick={() => handleTap(true)}>YES</button>
        <button className="yn-btn yn-btn--no"  disabled={submitting} onClick={() => handleTap(false)}>NO</button>
      </div>
    </div>
  )
}

// ── Inline-editable task row ───────────────────────────────────────────────
const PRIORITY_COLORS = { low: '#6b7280', normal: '#60a5fa', high: '#f87171' }

function TaskRow({ task, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft]     = useState({ title: task.title, priority: task.priority })

  const next        = { todo: 'in_progress', in_progress: 'done' }[task.status]
  const statusColor = task.status === 'in_progress' ? '#f59e0b' : '#6b7280'
  const statusLabel = task.status === 'in_progress' ? 'IN PROGRESS' : 'TO DO'

  const saveEdit = () => {
    if (draft.title.trim()) {
      onUpdate({ title: draft.title.trim(), priority: draft.priority })
    }
    setEditing(false)
  }

  const cancelEdit = () => {
    setDraft({ title: task.title, priority: task.priority })
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="room-task-row room-task-row--editing">
        <input
          className="room-task-edit-input"
          value={draft.title}
          onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit() }}
          autoFocus
        />
        <select
          className="room-task-edit-priority"
          value={draft.priority}
          onChange={(e) => setDraft(d => ({ ...d, priority: e.target.value }))}
        >
          <option value="low">LOW</option>
          <option value="normal">NORMAL</option>
          <option value="high">HIGH</option>
        </select>
        <button className="room-task-edit-save" onClick={saveEdit} aria-label="Save"><CheckIcon width="16" height="16" /></button>
        <button className="room-task-edit-cancel" onClick={cancelEdit} aria-label="Cancel"><CloseIcon width="16" height="16" /></button>
      </div>
    )
  }

  return (
    <div className="room-task-row">
      <span
        className="wb-priority-dot"
        style={{ background: PRIORITY_COLORS[task.priority] ?? '#60a5fa', flexShrink: 0 }}
      />
      <span className="room-task-title">{task.title}</span>
      <button
        className="room-task-status"
        style={{ color: statusColor, borderColor: statusColor }}
        onClick={() => onUpdate({ status: next })}
        title="Advance status"
      >
        {statusLabel}
      </button>
      <button
        className="room-task-edit-btn"
        onClick={() => { setDraft({ title: task.title, priority: task.priority }); setEditing(true) }}
        aria-label="Edit task"
      ><PencilIcon width="14" height="14" /></button>
      <button className="room-task-delete" onClick={onDelete} aria-label="Delete"><CloseIcon width="14" height="14" /></button>
    </div>
  )
}

// ── SOP Viewer Modal ───────────────────────────────────────────────────────
function SopViewerModal({ sop, onClose }) {
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel"
        style={{ maxWidth: 520, width: '95vw' }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-header">
          <span className="modal-glyph"><BoardIcon width="20" height="20" /></span>
          <span className="modal-title">{sop.id}</span>
          <span className="modal-room">v{sop.version}</span>
          <button className="modal-close" onClick={onClose}><CloseIcon width="16" height="16" /></button>
        </div>
        <div style={{ padding: '0 24px 24px' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: '#e0e0f0', marginBottom: 16 }}>
            {sop.title}
          </p>
          <ol className="sop-steps sop-steps--modal">
            {sop.steps.map((step, i) => (
              <li key={i} className="sop-step">{step}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── CHECKS tab — Pot Check + Filter Change ────────────────────────────────
function ChecksTab({ roomId }) {
  const [sub, setSub] = useState('pot')
  return (
    <section className="drawer-section">
      <div className="checks-sub-nav">
        <button className={`checks-sub-btn ${sub === 'pot' ? 'active' : ''}`} onClick={() => setSub('pot')}>
          <PlantIcon width="16" height="16" style={{marginRight: 6}} /> Pot Check
        </button>
        <button className={`checks-sub-btn ${sub === 'filter' ? 'active' : ''}`} onClick={() => setSub('filter')}>
          <WindIcon width="16" height="16" style={{marginRight: 6}} /> Filter Change
        </button>
      </div>
      {sub === 'pot' && (
        <YesNoLog
          key={`pot-${roomId}`}
          label="Pot Check"
          apiPath="/api/pot-check-logs"
          bodyFn={(completed) => ({ roomId, completed, rootHealth: completed ? 'healthy' : 'concern' })}
        />
      )}
      {sub === 'filter' && (
        <YesNoLog
          key={`filter-${roomId}`}
          label="Filter Change"
          apiPath="/api/filter-change-logs"
          bodyFn={(completed) => ({ roomId, completed, newInstalled: completed })}
        />
      )}
    </section>
  )
}

// ── PPE items config ───────────────────────────────────────────────────────
const BASE_PPE = [
  { id: 'gloves',  label: 'Nitrile Gloves',  icon: <GlovesIcon width="20" height="20" /> },
  { id: 'glasses', label: 'Safety Glasses',   icon: <GogglesIcon width="20" height="20" /> },
  { id: 'boots',   label: 'Rubber Boots',     icon: <BootsIcon width="20" height="20" /> },
]
const IPM_PPE = [
  { id: 'respirator', label: 'N95/P100 Respirator', icon: <MaskIcon width="20" height="20" />, required: true },
  { id: 'coverall',   label: 'Disposable Coverall',  icon: <VestIcon width="20" height="20" />, required: true },
  { id: 'faceshield', label: 'Face Shield',           icon: <ShieldIcon width="20" height="20" />, required: false },
]
const REENTRY_PPE = [
  { id: 'respirator', label: 'N95/P100 Respirator', icon: <MaskIcon width="20" height="20" />, required: true },
  { id: 'coverall',   label: 'Disposable Coverall',  icon: <VestIcon width="20" height="20" />, required: true },
]

function getPpeItems(symbols, hasReentry) {
  if (hasReentry) return [...BASE_PPE, ...REENTRY_PPE]
  if (symbols?.includes('ipm')) return [...BASE_PPE, ...IPM_PPE]
  return BASE_PPE
}

function getPpeContext(symbols, hasReentry) {
  if (hasReentry) return 'reentry'
  if (symbols?.includes('ipm')) return 'ipm'
  return 'standard'
}

// ── PPE tab ────────────────────────────────────────────────────────────────
function PPETab({ roomId, symbols, reEntryExpiresAt }) {
  const authUser    = useFacilityStore(s => s.authUser)
  const hasReentry  = reEntryExpiresAt && new Date(reEntryExpiresAt) > new Date()
  const items       = getPpeItems(symbols, hasReentry)
  const context     = getPpeContext(symbols, hasReentry)
  const [checked, setChecked]     = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [saved, setSaved]           = useState(false)
  const [error, setError]           = useState(null)
  const [notes, setNotes]           = useState('')

  const toggle = (id) => setChecked(c => ({ ...c, [id]: !c[id] }))
  const allRequired = items.filter(i => i.required !== false).every(i => checked[i.id])
  const anyChecked  = items.some(i => checked[i.id])

  const handleConfirm = async () => {
    const worn = items.filter(i => checked[i.id]).map(i => i.id)
    setSubmitting(true); setError(null)
    try {
      if (API_BASE) {
        await apiFetch('/api/ppe-logs', {
          method: 'POST',
          body: JSON.stringify({ roomId, itemsWorn: worn, context, notes: notes || undefined }),
        })
      }
      setSaved(true)
      setTimeout(() => { setSaved(false); setChecked({}); setNotes('') }, 6000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (saved) return (
    <section className="drawer-section">
      <div className="ppe-confirmed">
        <div className="ppe-confirmed-icon"><CheckIcon width="24" height="24" /></div>
        <div className="ppe-confirmed-text">PPE CONFIRMED</div>
        <div className="ppe-confirmed-sub">Entry logged for {authUser?.name ?? 'operator'}</div>
      </div>
    </section>
  )

  return (
    <section className="drawer-section">
      {hasReentry && (
        <div className="ppe-reentry-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <WarningIcon width="16" height="16" style={{marginRight: 8}} /> RE-ENTRY PERIOD ACTIVE — enhanced PPE required
        </div>
      )}
      {context === 'ipm' && !hasReentry && (
        <div className="ppe-context-banner ppe-context-banner--ipm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BugIcon width="16" height="16" style={{marginRight: 8}} /> IPM active — respiratory protection required
        </div>
      )}

      <h3 className="section-title">CONFIRM PPE</h3>
      <p className="ppe-instruction">Check each item worn before entering the room.</p>

      <div className="ppe-checklist">
        {items.map(item => (
          <label key={item.id} className={`ppe-item ${checked[item.id] ? 'ppe-item--checked' : ''}`}>
            <input
              type="checkbox"
              checked={!!checked[item.id]}
              onChange={() => toggle(item.id)}
            />
            <span className="ppe-item-icon">{item.icon}</span>
            <span className="ppe-item-label">{item.label}</span>
            {item.required && <span className="ppe-item-req">*</span>}
          </label>
        ))}
      </div>

      <div className="form-row" style={{ marginTop: 12 }}>
        <label>Notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          placeholder="Optional…"
        />
      </div>

      {error && <p className="form-error">{error}</p>}

      <button
        className="btn-primary"
        style={{ marginTop: 14, width: '100%' }}
        disabled={submitting || !anyChecked || !allRequired}
        onClick={handleConfirm}
      >
        {submitting ? 'Saving…' : 'Confirm Entry'}
      </button>
      {!allRequired && anyChecked && (
        <p className="ppe-warning">Required items (*) must be checked before confirming.</p>
      )}
    </section>
  )
}

// ── LifeChart Cultivation Tasks tab ───────────────────────────────────────
function LcTaskRow({ task, onComplete, onPostpone }) {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const handleComplete = async () => {
    setBusy(true)
    try { await onComplete(task.id); setDone(true) }
    catch { setBusy(false) }
  }
  const handlePostpone = async () => {
    setBusy(true)
    try { await onPostpone(task.id) } finally { setBusy(false) }
  }

  if (done) return (
    <div className="lc-task-row lc-task-row--done">
      <CheckIcon width="13" height="13" />
      <span className="lc-task-name">{task.name}</span>
      <span className="lc-task-done-label">DONE</span>
    </div>
  )

  return (
    <div className="lc-task-row">
      <span className="lc-task-color" style={{ background: task.color ?? 'var(--accent)' }} />
      <div className="lc-task-body">
        <span className="lc-task-name">{task.name}</span>
        <span className="lc-task-day">
          {task.strain_name ?? task.schedule_name ?? ''}
          {task.day_number != null ? ` · Day ${task.day_number}${task.end_day != null && task.end_day !== task.day_number ? `–${task.end_day}` : ''}` : ''}
        </span>
      </div>
      <div className="lc-task-actions">
        <button
          className="lc-task-btn lc-task-btn--postpone"
          disabled={busy}
          onClick={handlePostpone}
          title="Postpone"
        >↷</button>
        <button
          className="lc-task-btn lc-task-btn--complete"
          disabled={busy}
          onClick={handleComplete}
          title="Complete"
        ><CheckIcon width="13" height="13" /></button>
      </div>
    </div>
  )
}

function LcTasksTab({ roomId }) {
  const lcRoomMap      = useFacilityStore(s => s.lcRoomMap)
  const lcRoomMapReady = useFacilityStore(s => s.lcRoomMapReady)
  const lcProfile      = useFacilityStore(s => s.lcProfile)
  const loadLcProfile  = useFacilityStore(s => s.loadLcProfile)
  const lcRoomId       = lcRoomMap[roomId]
  const [tasks,        setTasks]       = useState(null)
  const [error,        setError]       = useState(null)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [retrying,     setRetrying]    = useState(false)

  const handleRetry = async () => {
    setRetrying(true)
    const token = await getToken()
    await loadLcProfile(token)
    setRetrying(false)
  }

  useEffect(() => {
    console.log(`[LC Tasks] roomId=${roomId} lcRoomMapReady=${lcRoomMapReady} lcRoomId=${lcRoomId ?? 'none'}`)
    if (!lcRoomId) return
    setTasks(null); setError(null); setScheduleOpen(false)
    console.log(`[LC Tasks] fetching tasks for lc_room_id=${lcRoomId}`)
    apiFetch(`/api/lifechart/tasks?lc_room_id=${lcRoomId}`)
      .then(data => {
        console.log(`[LC Tasks] received ${Array.isArray(data) ? data.length : 'non-array'} tasks`)
        setTasks(Array.isArray(data) ? data : [])
      })
      .catch(err => {
        console.error('[LC Tasks] fetch error:', err.message)
        setError(err.message)
      })
  }, [lcRoomId])

  const handleComplete = async (taskId) => {
    await apiFetch(`/api/lifechart/tasks/${taskId}/complete`, { method: 'POST' })
    setTasks(t => t.filter(x => x.id !== taskId))
  }
  const handlePostpone = async (taskId) => {
    await apiFetch(`/api/lifechart/tasks/${taskId}/postpone`, { method: 'POST' })
  }

  // Map not ready yet — show spinner
  if (!lcRoomMapReady) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="lc-tasks-loading">Loading…</p>
    </section>
  )

  // Map ready but LC profile never loaded (network error on startup) — offer retry
  if (!lcRoomId && !lcProfile) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="lc-tasks-empty">Could not connect to LifeChart.</p>
      <button className="lc-retry-btn" onClick={handleRetry} disabled={retrying}>
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </section>
  )

  // Map ready but this room has no LC equivalent
  if (!lcRoomId) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="lc-tasks-empty">No LifeChart room linked.</p>
    </section>
  )

  // Room is mapped — waiting on tasks fetch
  if (tasks === null) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="lc-tasks-loading">Loading…</p>
    </section>
  )

  if (error) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="form-error">{error}</p>
    </section>
  )

  if (tasks.length === 0) return (
    <section className="drawer-section">
      <h3 className="section-title">CULTIVATION TASKS</h3>
      <p className="lc-tasks-empty">No active tasks for this room.</p>
    </section>
  )

  // Split: due now (current_day to current_day+2) vs upcoming
  const currentDay  = tasks[0]?.current_day ?? 0
  const activeTasks   = tasks.filter(t => t.day_number >= currentDay && t.day_number <= currentDay + 2)
  const upcomingTasks = tasks.filter(t => t.day_number > currentDay + 2)

  return (
    <section className="drawer-section">
      <div className="lc-tasks-header">
        <h3 className="section-title">CULTIVATION TASKS</h3>
        <span className="lc-tasks-day-chip">DAY {currentDay}</span>
      </div>

      {activeTasks.length > 0 ? (
        <>
          <p className="lc-tasks-sublabel">DUE NOW</p>
          <div className="lc-tasks-list">
            {activeTasks.map(task => (
              <LcTaskRow key={task.id} task={task} onComplete={handleComplete} onPostpone={handlePostpone} />
            ))}
          </div>
        </>
      ) : (
        <p className="lc-tasks-empty">No tasks due in the next 2 days.</p>
      )}

      {upcomingTasks.length > 0 && (
        <div className="lc-schedule-dropdown">
          <button
            className="lc-schedule-toggle"
            onClick={() => setScheduleOpen(o => !o)}
          >
            <span>{scheduleOpen ? '▾' : '▸'} UPCOMING SCHEDULE</span>
            <span className="lc-schedule-count">{upcomingTasks.length} task{upcomingTasks.length !== 1 ? 's' : ''}</span>
          </button>
          {scheduleOpen && (
            <div className="lc-tasks-list lc-tasks-list--upcoming">
              {upcomingTasks.map(task => (
                <LcTaskRow key={task.id} task={task} onComplete={handleComplete} onPostpone={handlePostpone} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

// ── SOPs tab ───────────────────────────────────────────────────────────────
function SOPsTab({ symbols }) {
  const sops = getRelevantSops(symbols)
  const [acknowledged, setAcknowledged] = useState({})

  return (
    <section className="drawer-section">
      <h3 className="section-title">STANDARD OPERATING PROCEDURES</h3>
      <p className="sop-subtitle">{sops.length} SOP{sops.length !== 1 ? 's' : ''} relevant to this room</p>

      <div className="sop-list">
        {sops.map(sop => {
          const ack = acknowledged[sop.id]
          return (
            <div key={sop.id} className={`sop-card ${ack ? 'sop-card--acked' : ''}`}>
              <div className="sop-card-header">
                <div className="sop-card-meta">
                  <span className="sop-id">{sop.id}</span>
                  <span className="sop-version">v{sop.version}</span>
                  {ack && <span className="sop-ack-chip"><CheckIcon width="14" height="14" style={{marginRight: 4}} /> Read</span>}
                </div>
                <div className="sop-card-title">{sop.title}</div>
              </div>
              <div className="sop-card-body">
                <ol className="sop-steps">
                  {sop.steps.map((step, i) => (
                    <li key={i} className="sop-step">{step}</li>
                  ))}
                </ol>
                {!ack && (
                  <button
                    className="btn-primary sop-ack-btn"
                    onClick={() => setAcknowledged(a => ({ ...a, [sop.id]: true }))}
                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                  >
                    <CheckIcon width="16" height="16" style={{marginRight: 6}} /> Acknowledge
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── drawerPotCount helper ──────────────────────────────────────────────────
function drawerPotCount(roomType, tableIndex) {
  if (roomType === 'flower') return 34
  return tableIndex < 4 ? 35 : 34
}

const WO_TYPES = [
  { value: 'pest',        label: 'Pest'        },
  { value: 'disease',     label: 'Disease'     },
  { value: 'deficiency',  label: 'Deficiency'  },
  { value: 'root',        label: 'Root'        },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other',       label: 'Other'       },
]

const WO_PRIORITIES = [
  { value: 'low',    label: 'Low'    },
  { value: 'normal', label: 'Normal' },
  { value: 'high',   label: 'High'   },
]

function WoForm({ woType, setWoType, woPriority, setWoPriority, woNotes, setWoNotes, woError, woSubmitting, onSubmit, onCancel }) {
  return (
    <div className="table-wo-form">
      <p className="issue-form-label">Type</p>
      <div className="wo-pills">
        {WO_TYPES.map(t => (
          <button
            key={t.value}
            className={`wo-pill${woType === t.value ? ' wo-pill--active' : ''}`}
            onClick={() => setWoType(t.value)}
          >{t.label}</button>
        ))}
      </div>

      <p className="issue-form-label">Priority</p>
      <div className="wo-pills">
        {WO_PRIORITIES.map(p => (
          <button
            key={p.value}
            className={`wo-pill wo-pill--priority-${p.value}${woPriority === p.value ? ' wo-pill--active' : ''}`}
            onClick={() => setWoPriority(p.value)}
          >{p.label}</button>
        ))}
      </div>

      <textarea
        className="wo-notes"
        placeholder="Notes (optional)"
        value={woNotes}
        onChange={e => setWoNotes(e.target.value)}
        rows={2}
      />

      {woError && <p className="issue-form-error">{woError}</p>}

      <div className="wo-form-actions">
        <button className="wo-submit-btn" onClick={onSubmit} disabled={woSubmitting}>
          {woSubmitting ? 'Saving…' : 'Create'}
        </button>
        <button className="wo-cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── Tables + Work Orders tab ───────────────────────────────────────────────
function TablesWorkOrdersTab({ room, authUser, cardRefs }) {
  const lcRoomMap        = useFacilityStore(s => s.lcRoomMap)
  const workOrders       = useFacilityStore(s => s.workOrders)
  const loadWorkOrders   = useFacilityStore(s => s.loadWorkOrders)
  const addWorkOrder     = useFacilityStore(s => s.addWorkOrder)
  const resolveWorkOrder = useFacilityStore(s => s.resolveWorkOrder)
  const activeTable      = useFacilityStore(s => s.activeTable)

  const lcRoomId = lcRoomMap[room.id]

  const [equipment, setEquipment]        = useState(null)
  const [equipError, setEquipError]      = useState(false)
  const [openWoContext, setOpenWoContext] = useState(null) // string | null
  const [woType, setWoType]              = useState('')
  const [woPriority, setWoPriority]      = useState('')
  const [woNotes, setWoNotes]            = useState('')
  const [woSubmitting, setWoSubmitting]  = useState(false)
  const [woError, setWoError]            = useState('')
  const [collapsed, setCollapsed]        = useState({}) // Record<groupKey, boolean>

  useEffect(() => {
    if (!lcRoomId) return
    loadWorkOrders(lcRoomId)
    apiFetch(`/api/lifechart/rooms/${lcRoomId}/detail`)
      .then(data => setEquipment(data.equipment ?? []))
      .catch(() => setEquipError(true))
  }, [lcRoomId, loadWorkOrders])

  function openForm(context) {
    setOpenWoContext(context)
    setWoType('')
    setWoPriority('')
    setWoNotes('')
    setWoError('')
  }

  function closeForm() {
    setOpenWoContext(null)
    setWoError('')
  }

  async function handleSubmit() {
    if (!woType || !woPriority) {
      setWoError('Select type and priority.')
      return
    }
    setWoSubmitting(true)
    setWoError('')
    try {
      const fullNotes = [openWoContext, woNotes].filter(Boolean).join(': ')
      await addWorkOrder({ lcRoomId, type: woType, priority: woPriority, notes: fullNotes })
      closeForm()
    } catch {
      setWoError('Failed to create work order.')
    } finally {
      setWoSubmitting(false)
    }
  }

  function toggleGroup(key) {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const openOrders     = workOrders[lcRoomId] ?? []
  const isAdmin        = authUser?.role === 'admin'
  const hasTablesGroup = room.type === 'flower' || room.type === 'veg'

  // Build LC equipment groups keyed by type
  const lcGroups = []
  if (!equipError && equipment !== null && equipment.length > 0) {
    const byType = {}
    equipment.forEach((eq, idx) => {
      const type = eq.type ?? eq.device_type ?? 'Other'
      if (!byType[type]) byType[type] = []
      byType[type].push({ ...eq, _idx: idx })
    })
    Object.keys(byType).sort().forEach(type => {
      lcGroups.push({ key: type, label: type.toUpperCase(), items: byType[type] })
    })
  }

  return (
    <>
      <div className="equipment-accordion">

        {/* ── Growing Tables group ─────────────────────────────────────── */}
        <div className={`equipment-group${collapsed['growing-tables'] ? ' equipment-group--collapsed' : ''}`}>
          <div className="equipment-group-header" onClick={() => toggleGroup('growing-tables')}>
            <div className="equipment-group-left">
              <span className="equipment-group-label">GROWING TABLES</span>
              {hasTablesGroup && <span className="equipment-group-count">6</span>}
            </div>
            <span className="equipment-group-chevron">▾</span>
          </div>
          <div className="equipment-group-body">
            {!hasTablesGroup ? (
              <p className="drawer-empty">No tables in this room type.</p>
            ) : (
              <div className="tables-list">
                {Array.from({ length: 6 }, (_, i) => {
                  const pots       = drawerPotCount(room.type, i)
                  const isActive   = activeTable?.roomId === room.id && activeTable?.tableIndex === i
                  const context    = `Table ${i + 1}`
                  const isFormOpen = openWoContext === context
                  return (
                    <div
                      key={i}
                      ref={el => { if (cardRefs) cardRefs.current[i] = el }}
                      className={['table-card', isActive ? 'table-card--active' : ''].filter(Boolean).join(' ')}
                    >
                      <div className="table-card-header">
                        <span>Table {i + 1}</span>
                        <button
                          className={`table-wo-btn${isFormOpen ? ' table-wo-btn--active' : ''}`}
                          onClick={() => isFormOpen ? closeForm() : openForm(context)}
                        >+ WO</button>
                      </div>
                      <div className="table-pot-grid" style={{ '--pot-cols': Math.ceil(pots / 2) }}>
                        {Array.from({ length: pots }, (_, j) => (
                          <div key={j} className="table-pot table-pot--filled" />
                        ))}
                      </div>
                      <div className="table-card-count">{pots} plants</div>
                      {isFormOpen && (
                        <WoForm
                          woType={woType} setWoType={setWoType}
                          woPriority={woPriority} setWoPriority={setWoPriority}
                          woNotes={woNotes} setWoNotes={setWoNotes}
                          woError={woError} woSubmitting={woSubmitting}
                          onSubmit={handleSubmit} onCancel={closeForm}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── LC Equipment groups ──────────────────────────────────────── */}
        {!lcRoomId && (
          <div className="equipment-group">
            <div className="equipment-group-header">
              <div className="equipment-group-left">
                <span className="equipment-group-label">EQUIPMENT</span>
              </div>
            </div>
            <div className="equipment-group-body">
              <p className="drawer-empty">No LifeChart connection for this room.</p>
            </div>
          </div>
        )}
        {lcRoomId && equipError && (
          <div className="equipment-group">
            <div className="equipment-group-header">
              <div className="equipment-group-left">
                <span className="equipment-group-label">EQUIPMENT</span>
              </div>
            </div>
            <div className="equipment-group-body">
              <p className="drawer-empty">Could not load equipment.</p>
            </div>
          </div>
        )}
        {lcRoomId && !equipError && equipment === null && (
          <div className="equipment-group">
            <div className="equipment-group-header">
              <div className="equipment-group-left">
                <span className="equipment-group-label">EQUIPMENT</span>
              </div>
            </div>
            <div className="equipment-group-body">
              <p className="drawer-empty">Loading…</p>
            </div>
          </div>
        )}
        {lcRoomId && !equipError && equipment !== null && equipment.length === 0 && (
          <div className="equipment-group">
            <div className="equipment-group-header">
              <div className="equipment-group-left">
                <span className="equipment-group-label">EQUIPMENT</span>
              </div>
            </div>
            <div className="equipment-group-body">
              <p className="drawer-empty">No equipment listed for this room.</p>
            </div>
          </div>
        )}
        {lcGroups.map(group => (
          <div
            key={group.key}
            className={`equipment-group${collapsed[group.key] ? ' equipment-group--collapsed' : ''}`}
          >
            <div className="equipment-group-header" onClick={() => toggleGroup(group.key)}>
              <div className="equipment-group-left">
                <span className="equipment-group-label">{group.label}</span>
                <span className="equipment-group-count">{group.items.length}</span>
              </div>
              <span className="equipment-group-chevron">▾</span>
            </div>
            <div className="equipment-group-body">
              {group.items.map(eq => {
                const name       = eq.name ?? eq.device_name ?? 'Unknown Device'
                const serial     = eq.serial_number ?? null
                const status     = eq.status ?? null
                const isFormOpen = openWoContext === name
                return (
                  <div key={eq.id ?? eq._idx} className="equipment-item">
                    <div className="equipment-item-header">
                      <div>
                        <span className="equipment-item-name">{name}</span>
                        {(serial || status) && (
                          <div className="equipment-item-meta">
                            {serial && <span className="equipment-item-serial">{serial}</span>}
                            {status && <span className="equipment-item-status">{status}</span>}
                          </div>
                        )}
                      </div>
                      <button
                        className={`table-wo-btn${isFormOpen ? ' table-wo-btn--active' : ''}`}
                        onClick={() => isFormOpen ? closeForm() : openForm(name)}
                      >+ WO</button>
                    </div>
                    {isFormOpen && (
                      <WoForm
                        woType={woType} setWoType={setWoType}
                        woPriority={woPriority} setWoPriority={setWoPriority}
                        woNotes={woNotes} setWoNotes={setWoNotes}
                        woError={woError} woSubmitting={woSubmitting}
                        onSubmit={handleSubmit} onCancel={closeForm}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}

      </div>

      {/* ── Open work orders panel ───────────────────────────────────── */}
      {lcRoomId && (
        <section className="open-wo-panel">
          <h3 className="section-title">OPEN WORK ORDERS</h3>
          {openOrders.length === 0 ? (
            <p className="drawer-empty">No open work orders.</p>
          ) : (
            <div className="wo-list">
              {openOrders.map(wo => (
                <div key={wo.id} className={`wo-item wo-item--${wo.priority ?? 'normal'}`}>
                  <div className="wo-item-header">
                    <span className="wo-item-type">{wo.type}</span>
                    <span className={`wo-item-badge wo-item-badge--${wo.priority ?? 'normal'}`}>
                      {wo.priority ?? 'normal'}
                    </span>
                    {isAdmin && (
                      <button
                        className="wo-resolve-btn"
                        onClick={() => resolveWorkOrder(wo.id, lcRoomId)}
                      >Resolve</button>
                    )}
                  </div>
                  {wo.notes && <p className="wo-item-notes">{wo.notes}</p>}
                  <div className="wo-item-meta">
                    {wo.created_at && new Date(wo.created_at).toLocaleString('en-CA', {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  )
}

// ── Main Drawer ────────────────────────────────────────────────────────────
export default function RoomDrawer() {
  const { selectedRoomId, drawerOpen, closeDrawer, getRoom, updateRoomStatus, updateRoomMode, transfers, tasks, updateTask, deleteTask } = useFacilityStore()
  const activeTable       = useFacilityStore(s => s.activeTable)
  const authUser           = useFacilityStore(s => s.authUser)
  const theme = useTheme()
  const STATUS_COLORS = STATUS_COLORS_BY_THEME[theme] ?? STATUS_COLORS_BY_THEME['night-mode']
  const room = selectedRoomId ? getRoom(selectedRoomId) : null
  const roomTasks = room ? tasks.filter(t => t.roomId === room.id && t.status !== 'done') : []
  const transferAsOrigin = room ? transfers[room.id] : null
  const transferAsDest   = room
    ? Object.entries(transfers).find(([, t]) => t.destinationId === room.id)
    : null
  const sc = room ? STATUS_COLORS[room.status] ?? STATUS_COLORS[STATUS.NORMAL] : STATUS_COLORS[STATUS.NORMAL]
  const tc = room ? (TYPE_COLOR[room.type] || TYPE_COLOR.utility) : TYPE_COLOR.utility

  const [tab, setTab]               = useState('OVERVIEW')
  const [defolOpen, setDefolOpen]   = useState(false)
  const [sprayOpen, setSprayOpen]   = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [netOpen, setNetOpen]       = useState(false)
  const [sprayKey, setSprayKey]     = useState(0)
  const [sopViewing, setSopViewing] = useState(null) // sop object | null

  const cardRefs = useRef([])

  const hasDefoliation = room?.symbols?.includes('defoliation')

  useEffect(() => {
    if (!activeTable || activeTable.roomId !== room?.id) return
    setTab('TABLES')
    const idx = activeTable.tableIndex
    const tid = setTimeout(() => {
      cardRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 80)
    return () => clearTimeout(tid)
  }, [activeTable, room?.id])

  return (
    <>
      <div className={`drawer-backdrop ${drawerOpen ? 'open' : ''}`} onClick={closeDrawer} aria-hidden />

      <aside className={`room-drawer ${drawerOpen ? 'open' : ''}`} aria-label="Room detail panel">
        {room && (
          <>
            {/* ── Header ──────────────────────────────────────────── */}
            <div className="drawer-header" style={{ borderBottomColor: sc.border }}>
              <div className="drawer-header-top">
                <div className="drawer-status-dot" style={{ background: sc.label }} />
                <h2 className="drawer-room-name">{room.name}</h2>
                <span className="drawer-status-badge"
                  style={{ color: sc.label, borderColor: sc.border, background: sc.bg }}>
                  {STATUS_LABEL[room.status] ?? room.status.toUpperCase()}
                </span>
                <button className="drawer-close" onClick={closeDrawer}><CloseIcon width="16" height="16" /></button>
              </div>

              <div className="drawer-meta">
                <span className="meta-chip">
                  <span className="meta-label">TYPE</span>
                  <span className="meta-value" style={{ color: tc.label }}>{room.type?.toUpperCase()}</span>
                </span>
                {room.stage && (
                  <span className="meta-chip">
                    <span className="meta-label">STAGE</span>
                    <span className="meta-value">{room.stage}</span>
                  </span>
                )}
                <span className="meta-chip meta-chip-select">
                  <span className="meta-label">MODE</span>
                  <select
                    className="mode-select"
                    value={room.mode ?? MODES.OFF}
                    onChange={(e) => updateRoomMode(room.id, e.target.value)}
                  >
                    {MODE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </span>
              </div>
            </div>

            {/* ── Tabs ─────────────────────────────────────────────── */}
            <div className="drawer-tabs">
              {DRAWER_TABS.map(t => (
                <button
                  key={t}
                  className={`drawer-tab ${tab === t ? 'active' : ''}`}
                  onClick={() => setTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* ── Tab content (scrollable) ─────────────────────────── */}
            <div className="drawer-tab-content">

            {/* ── Tab: Overview ────────────────────────────────────── */}
            {tab === 'OVERVIEW' && (
              <>
                {room.symbols?.length > 0 && (
                  <section className="drawer-section">
                    <h3 className="section-title">ACTIVE FLAGS</h3>
                    <div className="flags-grid">
                      {room.symbols.map(sym => {
                        const s   = SYMBOL_DISPLAY[sym]
                        if (!s) return null
                        const isDefol = sym === 'defoliation'
                        const isNet   = sym === 'net'
                        const sop     = getSopForOverlay(sym)
                        return (
                          <div key={sym} className={`flag-chip ${(isDefol || isNet) ? 'flag-chip--editable' : ''}`}>
                            <span className="flag-glyph">{s.glyph}</span>
                            <span className="flag-label">{s.label}</span>
                            {sop && (
                              <button
                                className="flag-sop-btn"
                                onClick={() => setSopViewing(sop)}
                                aria-label={`View SOP for ${s.label}`}
                              >
                                SOP
                              </button>
                            )}
                            {isDefol && (
                              <button className="flag-edit-btn" onClick={() => setDefolOpen(true)}
                                aria-label="Edit defoliation progress"><PencilIcon width="12" height="12" /></button>
                            )}
                            {isNet && (
                              <button className="flag-edit-btn" onClick={() => setNetOpen(true)}
                                aria-label="Log net status"><PencilIcon width="12" height="12" /></button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </section>
                )}

                {roomTasks.length > 0 && (
                  <section className="drawer-section">
                    <h3 className="section-title">OPEN TASKS</h3>
                    <div className="room-tasks-list">
                      {roomTasks.map(task => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          onUpdate={(patch) => {
                            updateTask(task.id, patch)
                            apiFetch(`/api/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify(patch) })
                              .catch(() => updateTask(task.id, task))
                          }}
                          onDelete={() => {
                            deleteTask(task.id)
                            apiFetch(`/api/tasks/${task.id}`, { method: 'DELETE' }).catch(() => {})
                          }}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {(transferAsOrigin || transferAsDest) && (
                  <section className="drawer-section">
                    <h3 className="section-title">ACTIVE TRANSFER</h3>
                    {transferAsOrigin && (
                      <div className="transfer-info-card">
                        <div className="transfer-info-row">
                          <span className="transfer-info-label">TO</span>
                          <span className="transfer-info-value" style={{ color: '#f59e0b' }}>
                            {getRoom(transferAsOrigin.destinationId)?.name ?? transferAsOrigin.destinationId}
                          </span>
                        </div>
                        {transferAsOrigin.transferType && (
                          <div className="transfer-info-row">
                            <span className="transfer-info-label">TYPE</span>
                            <span className="transfer-info-value">{transferAsOrigin.transferType}</span>
                          </div>
                        )}
                        {transferAsOrigin.transferDate && (
                          <div className="transfer-info-row">
                            <span className="transfer-info-label">DATE</span>
                            <span className="transfer-info-value">
                              {new Date(transferAsOrigin.transferDate).toLocaleString('en-CA', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
                              })}
                            </span>
                          </div>
                        )}
                        {transferAsOrigin.notes && (
                          <p className="transfer-info-notes">{transferAsOrigin.notes}</p>
                        )}
                        <button className="transfer-edit-btn" onClick={() => setTransferOpen(true)}>
                          <PencilIcon width="12" height="12" style={{marginRight: 6}} /> Edit Transfer
                        </button>
                      </div>
                    )}
                    {transferAsDest && !transferAsOrigin && (
                      <div className="transfer-info-card transfer-info-card--incoming">
                        <div className="transfer-info-row">
                          <span className="transfer-info-label">FROM</span>
                          <span className="transfer-info-value" style={{ color: '#f59e0b' }}>
                            {getRoom(transferAsDest[0])?.name ?? transferAsDest[0]}
                          </span>
                        </div>
                        <p className="transfer-info-notes">Incoming transfer — edit from origin room.</p>
                      </div>
                    )}
                  </section>
                )}

                {room.id === 'PREVEG' && <PreVegZoneMap />}

                <section className="drawer-section">
                  <h3 className="section-title" style={{ opacity: 0.4 }}>DEV – STATUS OVERRIDE</h3>
                  <div className="status-override-row">
                    {Object.entries(STATUS_COLORS).map(([s, c]) => (
                      <button key={s}
                        className={`status-btn ${room.status === s ? 'active' : ''}`}
                        style={{ borderColor: c.border, color: c.label, background: room.status === s ? c.bg : 'transparent' }}
                        onClick={() => updateRoomStatus(room.id, s)}
                      >
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </section>
              </>
            )}

            {/* ── Tab: Tasks (LifeChart) ───────────────────────────── */}
            {tab === 'TASKS' && (
              <LcTasksTab key={room.id} roomId={room.id} />
            )}

            {/* ── Tab: Spray Logs ──────────────────────────────────── */}
            {tab === 'SPRAY LOGS' && (
              <section className="drawer-section">
                <div className="section-header-row">
                  <h3 className="section-title">SPRAY LOGS</h3>
                  <button className="action-btn-sm" onClick={() => setSprayOpen(true)}>+ New</button>
                </div>
                <SprayLogList key={sprayKey} roomId={room.id} />
              </section>
            )}

            {/* ── Tab: Checks ──────────────────────────────────────── */}
            {tab === 'CHECKS' && (
              <ChecksTab key={room.id} roomId={room.id} />
            )}

            {/* ── Tab: PPE ─────────────────────────────────────────── */}
            {tab === 'PPE' && (
              <PPETab
                key={room.id}
                roomId={room.id}
                symbols={room.symbols}
                reEntryExpiresAt={room.reEntryExpiresAt}
              />
            )}

            {/* ── Tab: SOPs ────────────────────────────────────────── */}
            {tab === 'SOPs' && (
              <SOPsTab key={room.id} symbols={room.symbols} />
            )}

            {/* ── Tab: Tables ──────────────────────────────────────── */}
            {tab === 'TABLES' && (
              <div className="drawer-section">
                <TablesWorkOrdersTab
                  key={room.id}
                  room={room}
                  authUser={authUser}
                  cardRefs={cardRefs}
                />
              </div>
            )}

            </div>{/* end drawer-tab-content */}
          </>
        )}
      </aside>

      {defolOpen && room && hasDefoliation && (
        <DefoliationModal roomId={room.id} roomName={room.name} onClose={() => setDefolOpen(false)} />
      )}
      {sprayOpen && room && (
        <SprayLogModal
          roomId={room.id}
          onClose={() => setSprayOpen(false)}
          onSaved={() => { setSprayKey(k => k + 1); setTab('SPRAY LOGS') }}
        />
      )}
      {transferOpen && room && transferAsOrigin && (
        <TransferModal originRoomId={room.id} onClose={() => setTransferOpen(false)} />
      )}
      {netOpen && room && (
        <NetModal roomId={room.id} onClose={() => setNetOpen(false)} onSaved={() => setNetOpen(false)} />
      )}
      {sopViewing && (
        <SopViewerModal sop={sopViewing} onClose={() => setSopViewing(null)} />
      )}
    </>
  )
}
